// Which pages of a connected Notion workspace each of the organization's
// tenants uses: include/exclude scopes over the pages and databases shared
// with the app, each for triggers, agents or both. Saving replaces the
// tenant's scopes on this connection.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import ConfirmDialog from "../../../../components/ConfirmDialog.tsx";
import SearchField from "../../../../components/SearchField.tsx";
import {
  api,
  getErrorMessage,
  type NotionConnection,
  type NotionObject,
  type NotionScope,
  type NotionScopeInput,
} from "../../../../api/client.ts";
import { keys } from "../../../../api/queryKeys.ts";
import ScrollTable from "../../../../components/table/ScrollTable.tsx";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue.ts";

// A scope being edited: what is saved plus what the table shows.
type DraftScope = NotionScopeInput & { title: string; url: string | null };

const toDraft = (s: NotionScope): DraftScope => ({
  notion_object_id: s.notion_object_id,
  object_type: s.object_type,
  mode: s.mode,
  include_subpages: s.include_subpages,
  use_for_triggers: s.use_for_triggers,
  use_for_agents: s.use_for_agents,
  company_property: s.company_property,
  title: s.title,
  url: s.url,
});

const toInput = ({ title: _title, url: _url, ...input }: DraftScope): NotionScopeInput => ({
  ...input,
  company_property: input.company_property?.trim() || null,
});

const fromObject = (o: NotionObject): DraftScope => ({
  notion_object_id: o.notion_id,
  object_type: o.object_type,
  mode: "include",
  include_subpages: true,
  use_for_triggers: true,
  use_for_agents: true,
  company_property: null,
  title: o.title,
  url: o.url,
});

const sameScopes = (a: DraftScope[], b: DraftScope[]) =>
  JSON.stringify(a.map(toInput)) === JSON.stringify(b.map(toInput));

interface NotionScopesDialogProps {
  organizationId: string;
  // null = closed. Remount (key) per connection so nothing carries over.
  connection: NotionConnection | null;
  // The tenants this organization owns: only those can be given pages.
  tenants: { id: string; name: string }[];
  onClose: () => void;
}

export default function NotionScopesDialog({
  organizationId,
  connection,
  tenants,
  onClose,
}: NotionScopesDialogProps) {
  const queryClient = useQueryClient();
  const open = !!connection;
  const connectionId = connection?.id ?? "";
  const [pickedTenantId, setPickedTenantId] = useState("");
  const tenantId = pickedTenantId || tenants[0]?.id || "";
  // The scopes being edited, and which connection/tenant they were loaded for.
  const [draft, setDraft] = useState<DraftScope[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search.trim(), 300);

  const scopes = useQuery({
    queryKey: keys.notion.scopes(connectionId, tenantId),
    queryFn: () => api.getNotionScopes(organizationId, connectionId, tenantId),
    enabled: open && !!tenantId,
  });
  const loadKey = `${connectionId}:${tenantId}`;
  // Start editing from the saved scopes once they land; a refetch never
  // clobbers edits in progress.
  if (scopes.data && loadedFor !== loadKey) {
    setDraft(scopes.data.map(toDraft));
    setLoadedFor(loadKey);
  }
  const loaded = loadedFor === loadKey;
  const saved = scopes.data?.map(toDraft) ?? [];
  const dirty = loaded && !sameScopes(draft, saved);

  const objects = useQuery({
    queryKey: keys.notion.objects(connectionId, query),
    queryFn: () => api.searchNotionObjects(organizationId, connectionId, query),
    enabled: open,
    // Keep the last results on screen while the next search runs.
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: () => api.replaceNotionScopes(organizationId, connectionId, tenantId, draft.map(toInput)),
    onSuccess: (result) => {
      queryClient.setQueryData(keys.notion.scopes(connectionId, tenantId), result);
      setDraft(result.map(toDraft));
      // The workflow editor's Notion choices come with the data types and sources.
      void queryClient.invalidateQueries({ queryKey: keys.workflowDataType.all });
      void queryClient.invalidateQueries({ queryKey: keys.agentDefinition.all });
      toast.success("Notion pages saved");
    },
  });

  // Closing or switching tenant with unsaved changes asks first; this runs once they agree.
  const [discarding, setDiscarding] = useState<(() => void) | null>(null);
  const unlessDirty = (then: () => void) => {
    if (dirty) setDiscarding(() => then);
    else then();
  };
  const close = () => unlessDirty(onClose);
  const pickTenant = (id: string) => {
    if (id === tenantId) return;
    unlessDirty(() => setPickedTenantId(id));
  };
  const patch = (i: number, p: Partial<DraftScope>) =>
    setDraft((rows) => rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const added = new Set(draft.map((s) => s.notion_object_id));

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>
        Notion pages for tenants: {connection?.workspace_name || connection?.workspace_id}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Only pages and databases shared with the ScaleupXQ app in Notion appear here (in
            Notion, add the app under a page's Connections). A page is used for this tenant only
            when an include scope covers it and no exclude scope does. Triggers watch the pages for
            changes; agents may read them.
          </Typography>

          {tenants.length === 0 ? (
            <Alert severity="info">This organization owns no tenants to give pages to.</Alert>
          ) : (
            <>
              <TextField
                label="Tenant"
                select
                size="small"
                value={tenantId}
                onChange={(e) => pickTenant(e.target.value)}
                sx={{ maxWidth: 320 }}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>

              {scopes.error ? (
                <Alert severity="error">{getErrorMessage(scopes.error)}</Alert>
              ) : !loaded ? (
                <CircularProgress size={24} />
              ) : draft.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No pages for this tenant yet. Add some below.
                </Typography>
              ) : (
                <Box>
                  <ScrollTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>Page</TableCell>
                        <TableCell sx={{ width: 120 }}>Scope</TableCell>
                        <TableCell sx={{ width: 90 }}>Subpages</TableCell>
                        <TableCell sx={{ width: 80 }}>Triggers</TableCell>
                        <TableCell sx={{ width: 80 }}>Agents</TableCell>
                        <TableCell sx={{ width: 180 }}>Company property</TableCell>
                        <TableCell sx={{ width: 48 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {draft.map((s, i) => {
                        const isDatabase = s.object_type === "data_source";
                        return (
                          <TableRow key={s.notion_object_id}>
                            <TableCell>
                              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                {s.url ? (
                                  <Link href={s.url} target="_blank" rel="noopener noreferrer">
                                    {s.title || "Untitled"}
                                  </Link>
                                ) : (
                                  <span>{s.title || "Untitled"}</span>
                                )}
                                {isDatabase && <Chip size="small" variant="outlined" label="Database" />}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                value={s.mode}
                                onChange={(e) => patch(i, { mode: e.target.value as DraftScope["mode"] })}
                                fullWidth
                              >
                                <MenuItem value="include">Include</MenuItem>
                                <MenuItem value="exclude">Exclude</MenuItem>
                              </TextField>
                            </TableCell>
                            <TableCell>
                              {isDatabase ? (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  —
                                </Typography>
                              ) : (
                                <Switch
                                  size="small"
                                  checked={s.include_subpages}
                                  onChange={(e) => patch(i, { include_subpages: e.target.checked })}
                                  slotProps={{ input: { "aria-label": "Include subpages" } }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={s.use_for_triggers}
                                onChange={(e) => patch(i, { use_for_triggers: e.target.checked })}
                                slotProps={{ input: { "aria-label": "Use for triggers" } }}
                              />
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={s.use_for_agents}
                                onChange={(e) => patch(i, { use_for_agents: e.target.checked })}
                                slotProps={{ input: { "aria-label": "Use for agents" } }}
                              />
                            </TableCell>
                            <TableCell>
                              {isDatabase ? (
                                <TextField
                                  size="small"
                                  placeholder="e.g. Website"
                                  value={s.company_property ?? ""}
                                  onChange={(e) => patch(i, { company_property: e.target.value })}
                                  fullWidth
                                />
                              ) : (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                aria-label="Remove"
                                onClick={() => setDraft((rows) => rows.filter((_, j) => j !== i))}
                              >
                                <Trash2 fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </ScrollTable>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Company property (databases): the property holding the company's website or
                    email, so each page is matched to a company.
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add pages
                </Typography>
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search shared pages and databases by title"
                  fullWidth
                  clearable
                />
                {objects.error ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {getErrorMessage(objects.error)}
                  </Alert>
                ) : objects.isLoading ? (
                  <CircularProgress size={20} sx={{ mt: 1 }} />
                ) : (objects.data ?? []).length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                    {query
                      ? "Nothing shared with the app matches."
                      : "Nothing is shared with the ScaleupXQ app in this workspace yet."}
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 240, overflow: "auto" }}>
                    {(objects.data ?? []).map((o) => (
                      <ListItem
                        key={o.notion_id}
                        secondaryAction={
                          <Button
                            size="small"
                            disabled={added.has(o.notion_id) || !loaded}
                            onClick={() => setDraft((rows) => [...rows, fromObject(o)])}
                          >
                            {added.has(o.notion_id) ? "Added" : "Add"}
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={o.title || "Untitled"}
                          secondary={o.object_type === "data_source" ? "Database" : "Page"}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{dirty ? "Cancel" : "Close"}</Button>
        <Button
          variant="contained"
          disabled={!dirty || !tenantId}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={discarding !== null}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        color="warning"
        onConfirm={() => {
          discarding?.();
          setDiscarding(null);
        }}
        onClose={() => setDiscarding(null)}
      >
        Your changes to this tenant's pages have not been saved.
      </ConfirmDialog>
    </Dialog>
  );
}
